import type { MetaFunction } from '@remix-run/node'

import { Code, Entry, Method, ParamTable, Pill, Section } from '~/components/marketing/DocsKit'

export const meta: MetaFunction = () => [
  { title: 'REST API — Quiver Docs' },
  {
    name: 'description',
    content:
      'The key-authed Quiver REST API: create files, set status and data fields, read computed metrics, and inspect a workspace — all with a qvr_ bearer key.',
  },
]

const W = (
  <Pill tone="rose">write</Pill>
)
const R = <Pill tone="indigo">read</Pill>

export default function DocsApi() {
  return (
    <div className="space-y-14">
      <section>
        <p className="text-[11px] font-semibold uppercase tracking-wider text-indigo-300">Reference</p>
        <h1 className="mt-2 text-4xl font-bold tracking-tight text-white sm:text-5xl">REST API</h1>
        <p className="mt-5 max-w-2xl text-lg text-zinc-400">
          Plain HTTP over <span className="font-mono text-zinc-300">https://quiveros.ai/api/*</span>. Authenticate
          every request with a <span className="font-mono text-zinc-300">qvr_</span> workspace key. Reads are{' '}
          <span className="font-mono">GET</span>; writes are <span className="font-mono">POST/PATCH/DELETE</span> and need
          a write-capable key.
        </p>
        <div className="mt-6">
          <Code label="auth">{`Authorization: Bearer qvr_xxxxxxxxxxxx`}</Code>
        </div>
      </section>

      <Section eyebrow="Files" title="Create & list">
        <Entry header={<Method verb="POST" path="/api/files" />} pills={W} desc="Create a file of a configured type and populate its data in one call.">
          <ParamTable
            rows={[
              ['type', 'string · required', 'A file type from the workspace config (e.g. "score").'],
              ['title', 'string', 'Defaults to an auto title if omitted.'],
              ['data', 'object', 'Field id → value. Use an array for multi-value fields.'],
              ['parent', '{ id, field }', 'Append the new file to a parent file’s list field (a sub-item).'],
              ['status', 'string', 'Create directly in this status of the type’s workflow (recorded like a status move). An unknown id returns 422 listing the valid ones.'],
              ['force', 'boolean', 'Skip dedupe when the type de-duplicates on create.'],
              ['idempotency_key', 'string', 'Or the Idempotency-Key header (1-200 printable characters). A repeat with the same key in the same workspace returns the ORIGINAL file with replayed: true instead of creating another — so a create whose response was lost can be retried safely.'],
            ]}
          />
          <Code label="request → response">{`curl -X POST https://quiveros.ai/api/files \\
  -H "Authorization: Bearer $QUIVER_KEY" -H "content-type: application/json" \\
  -d '{"type":"score","title":"Alice · 250","data":{"player":"Alice","value":250}}'

{ "ok": true, "file": { "id": "cmq…", "type": "score", "title": "Alice · 250" } }`}</Code>
          <p className="text-sm text-zinc-400">
            Types that de-duplicate on create won’t spawn a near-duplicate: a confident match
            returns the existing file with <span className="font-mono text-zinc-300">reused: true</span>{' '}
            (your fields merged in — empty fields filled, lists unioned, conflicts reported);
            several plausible matches return <span className="font-mono text-zinc-300">409</span>{' '}
            with <span className="font-mono text-zinc-300">candidates</span>. Pass{' '}
            <span className="font-mono text-zinc-300">force: true</span> to create anyway.
          </p>
          <p className="text-sm text-zinc-400">
            A create is atomic: the file, its fields, its status and the parent link land together
            or not at all. If a step fails, what was written is rolled back and the response (500)
            says which step failed and that nothing was saved, with{' '}
            <span className="font-mono text-zinc-300">rolled_back: true</span> and{' '}
            <span className="font-mono text-zinc-300">retryable: true</span>.
          </p>
        </Entry>

        <Entry
          header={<Method verb="POST" path="/api/files/batch" />}
          pills={W}
          desc="Create many files in one request: up to 200 items, each exactly like a POST /api/files body, applied in order."
        >
          <ParamTable
            rows={[
              ['items', 'array · required', 'Create bodies (type, title, data, parent, status, force, idempotency_key). At most 200 (413 above).'],
              ['atomic', 'boolean', 'All or nothing: the first failure rolls back every file this request created and skips the rest. Default: every item is attempted and reported.'],
            ]}
          />
          <Code label="request → response">{`curl -X POST https://quiveros.ai/api/files/batch \\
  -H "Authorization: Bearer $QUIVER_KEY" -H "content-type: application/json" \\
  -d '{"items":[{"type":"task","title":"A","parent":{"id":"$P","field":"tasks"},"idempotency_key":"run1:0"},
               {"type":"task","title":"B","status":"nope"}]}'

{ "ok": false, "atomic": false, "created": 1, "failed": 1, "results": [
  { "index": 0, "ok": true, "status": 200, "file": { "id": "cmq…", "type": "task", "title": "A" } },
  { "index": 1, "ok": false, "status": 422, "error": "Unknown status 'nope' for type 'task'. Valid statuses: …" } ] }`}</Code>
        </Entry>

        <Entry header={<Method verb="GET" path="/api/files" />} pills={R} desc="List files, optionally filtered. Rows carry each type's summary fields by default; ?fields= overrides the projection.">
          <ParamTable
            rows={[
              ['type', 'string', 'Filter to one file type. Required when any filter/sort param below is used.'],
              ['status', 'string · repeatable', 'Filter by status id(s): ?status=playing&status=ended'],
              ['q', 'string', 'Title substring search.'],
              ['limit', 'number', 'Max rows. Default 100, capped at 1000.'],
              ['parent', 'file id', 'Only the files linked under this parent through its list link fields, in link order (response gains total and parent). Must be visible to you; children you can’t see are omitted. Composes with type, status, q, limit, offset, fields.'],
              ['parent_field', 'field id', 'With parent: only this link field.'],
              ['fields', 'csv', 'Data fields to include as KPIs: ?fields=value,category. OMITTED: each row carries its type’s summary_fields (what its list views show; a type without summary_fields falls back to its first three data fields). EMPTY (?fields=): status-only rows. Rows never include non-requested fields — fetch a file’s /kpis for everything.'],
            ]}
          />
          <Code label="response">{`curl "https://quiveros.ai/api/files?type=score&fields=player,value&limit=50" \\
  -H "Authorization: Bearer $QUIVER_KEY"

{ "files": [
  { "id": "cmq…", "title": "Alice · 250", "type": "score", "status": "ended",
    "kpis": [
      { "pathKey": "status", "stringValue": "ended" },
      { "pathKey": "dataFields.player", "stringValue": "Alice" },
      { "pathKey": "dataFields.value", "numericValue": 250 } ] } ] }`}</Code>
        </Entry>

        <Entry
          header={<Method verb="GET" path="/api/files (filtered query)" />}
          pills={R}
          desc={
            <>
              Adding any of the params below switches the list into a real query: filter by
              data-field <em>value</em>, by last-update time, sort, and page. Requires{' '}
              <span className="font-mono text-zinc-300">type</span>; the response gains a{' '}
              <span className="font-mono text-zinc-300">total</span> count for paging. The other
              list params (<span className="font-mono text-zinc-300">status</span>,{' '}
              <span className="font-mono text-zinc-300">q</span>,{' '}
              <span className="font-mono text-zinc-300">fields</span>,{' '}
              <span className="font-mono text-zinc-300">limit</span>) still apply.
            </>
          }
        >
          <ParamTable
            rows={[
              ['data.<field>', 'string · repeatable', 'Equality match on a data-field value: ?data.player=Alice. Repeat for AND: &data.daily_date=2026-07-01. Field ids are validated against the type’s config.'],
              ['updatedAt_gte', 'ISO or epoch-ms', 'Only files updated at/after this instant: ?updatedAt_gte=2026-07-01T00:00:00Z (or 1782172800000).'],
              ['updatedAt_lte', 'ISO or epoch-ms', 'Only files updated at/before this instant.'],
              ['sort', 'key:dir', 'updatedAt | createdAt | title, each :asc or :desc. Default updatedAt:desc. File columns only (no data-field sort).'],
              ['offset', 'number', 'Skip N rows (with limit = paging). Default 0.'],
            ]}
          />
          <Code label="request → response">{`curl "https://quiveros.ai/api/files?type=score&data.player=Alice&sort=updatedAt:desc&limit=20&offset=0" \\
  -H "Authorization: Bearer $QUIVER_KEY"

{ "files": [ { "id": "cmq…", "title": "Alice · 250", … } ], "total": 34 }`}</Code>
        </Entry>
      </Section>

      <Section eyebrow="Files" title="Read, update & delete a file">
        <Entry header={<Method verb="GET" path="/api/files/:id" />} pills={R} desc="Fetch one file with its full data and KPIs.">
          <Code label="response">{`{ "ok": true, "file": {
  "id": "cmq…", "title": "Alice · 250", "type": "score", "status": "ended",
  "data": "{\\"player\\":\\"Alice\\",\\"value\\":250}",
  "kpis": [ … ] } }`}</Code>
        </Entry>

        <Entry header={<Method verb="POST" path="/api/files/:id/data" />} pills={W} desc="Set one or more data fields (and optionally the title). Writes through the real KPI path, so metrics update immediately.">
          <ParamTable
            rows={[
              ['fields', 'object', 'Field id → new value. Batch-applied in one mutation.'],
              ['fieldId + value', 'string + any', 'Single-field alternative to fields: {"fieldId":"value","value":600}.'],
              ['title', 'string', 'Optionally rename the file.'],
              ['add / remove', '{ field: value | [values] }', 'Add or remove single elements of multi-valued fields (incl. file links), against the current list — concurrent adds all land. Link targets must be files you can see. Removing a link never deletes the linked file, even from a field that owns its children (the response notes it). One of fields, title, add, remove is required.'],
            ]}
          />
          <Code label="request → response">{`curl -X POST https://quiveros.ai/api/files/$ID/data \\
  -H "Authorization: Bearer $QUIVER_KEY" -H "content-type: application/json" \\
  -d '{"fields":{"value":600,"category":"hard"},"title":"Alice · 600"}'

{ "ok": true, "fields": { "value": 600, "category": "hard" }, "title": "Alice · 600",
  "sideEffects": [ { "fieldId": "score_delta", "name": "Score Δ", "value": 350 } ] }`}</Code>
          <p className="text-sm text-zinc-400">
            Writes are CRDT-safe: they merge with concurrent live edits instead of clobbering them,
            and metrics update immediately. Unknown/uneditable fields come back in{' '}
            <span className="font-mono text-zinc-300">skipped</span>; computed fields the write
            rippled into come back in <span className="font-mono text-zinc-300">sideEffects</span>.
            If nothing changes, the call is a no-op and returns{' '}
            <span className="font-mono text-zinc-300">400 “No fields applied”</span>.
          </p>
        </Entry>

        <Entry
          header={<Method verb="POST" path="/api/files/:id/status" />}
          pills={W}
          desc="Move a file to a status in its workflow (e.g. playing → ended). PATCH works too; body can be JSON ({status}) or form-encoded (status=…). An id that isn’t in the type’s workflow returns 422."
        >
          <Code label="request">{`curl -X POST https://quiveros.ai/api/files/$ID/status \\
  -H "Authorization: Bearer $QUIVER_KEY" -H "content-type: application/json" \\
  -d '{"status":"ended"}'

{ "ok": true, "fileId": "cmq…", "status": "ended", "sideEffects": [] }`}</Code>
        </Entry>

        <Entry header={<Method verb="DELETE" path="/api/files/:id" />} pills={W} desc="Permanently delete a file.">
          <Code label="request">{`curl -X DELETE https://quiveros.ai/api/files/$ID \\
  -H "Authorization: Bearer $QUIVER_KEY"
# → { "ok": true }`}</Code>
        </Entry>
      </Section>

      <Section
        eyebrow="Metrics"
        title="Read computed values"
        intro="You declare metrics in config (count, sum, max, grouped breakdowns, derived). The engine computes them — you just read the value."
      >
        <Entry
          header={<Method verb="GET" path="/api/metrics" />}
          pills={R}
          desc="With ?id= returns one metric (including its grouped breakdown); without, returns a snapshot of all metrics + a catalog. Key-only: this endpoint authenticates by qvr_ bearer key, never a session cookie."
        >
          <ParamTable
            rows={[
              ['id', 'string', 'A metric id from config. Omit for the full snapshot. The window/filter params below apply to single-metric reads.'],
              ['period', 'string', 'Request-time window: lifetime · 2026 · q1-2026 · w22-2026 · d2026-07-02. Omit to keep the metric’s own default window.'],
              ['from / to', 'YYYY-MM-DD', 'Custom date range (inclusive), instead of period.'],
              ['date', 'YYYY-MM-DD', 'One-day convenience — expands to from=to=date. Handy for daily boards.'],
              ['tz', 'IANA zone', 'Makes day windows follow local midnight: ?tz=America/Los_Angeles.'],
              ['limit', 'number', 'Top-N rows of a grouped metric (leaderboards). Capped at 1000; omit to use the metric’s configured limit.'],
              ['data.<field>', 'string · repeatable', 'Equality filter on a data-field value, applied at request time: ?data.daily_date=2026-07-01. Repeat for AND. Field ids validated against config.'],
            ]}
          />
          <Code label="daily leaderboard — one metric, any day">{`curl "https://quiveros.ai/api/metrics?id=top_players_daily&period=lifetime&data.daily_date=2026-07-01" \\
  -H "Authorization: Bearer $QUIVER_KEY"

{ "metric": {
  "id": "top_players_daily", "metric": 250,
  "breakdown": [
    { "key": "Alice", "value": 250 },
    { "key": "Bob",   "value": 175 } ] } }`}</Code>
          <p className="text-sm text-zinc-400">
            One configured metric serves every slice: the window params re-scope it per request
            (no per-day or per-week metric definitions), and{' '}
            <span className="font-mono text-zinc-300">data.&lt;field&gt;</span> filters do a plain
            value match — so a daily board keyed by a date <em>field</em> avoids time-zone drift
            entirely.
          </p>
        </Entry>
      </Section>

      <Section
        eyebrow="Recipes"
        title="Common queries"
        intro="Everything below is one GET — no fetch-everything-and-reduce client side."
      >
        <Entry header={<Method verb="GET" path="Daily leaderboard by date" />} pills={R} desc="A grouped metric filtered to one day’s files by a date field.">
          <Code label="request">{`curl "https://quiveros.ai/api/metrics?id=top_players_daily&period=lifetime&data.daily_date=2026-07-01&limit=10" \\
  -H "Authorization: Bearer $QUIVER_KEY"`}</Code>
        </Entry>

        <Entry header={<Method verb="GET" path="My records" />} pills={R} desc="All of one player’s files, newest first, with the values inlined.">
          <Code label="request">{`curl "https://quiveros.ai/api/files?type=score&data.player=Alice&sort=createdAt:desc&fields=value,daily_date&limit=25" \\
  -H "Authorization: Bearer $QUIVER_KEY"`}</Code>
        </Entry>

        <Entry header={<Method verb="GET" path="Presence — who’s on right now" />} pills={R} desc="Files in a live status that were touched in the last two minutes (epoch-ms works too).">
          <Code label="request">{`NOW=$(($(date +%s) * 1000)); SINCE=$((NOW - 120000))
curl "https://quiveros.ai/api/files?type=score&status=playing&updatedAt_gte=$SINCE" \\
  -H "Authorization: Bearer $QUIVER_KEY"`}</Code>
        </Entry>

        <Entry header={<Method verb="GET" path="Point lookup by several fields" />} pills={R} desc="Repeat data.<field> to AND conditions — e.g. one player’s entry for one day.">
          <Code label="request">{`curl "https://quiveros.ai/api/files?type=score&data.player=Alice&data.daily_date=2026-07-01&fields=value" \\
  -H "Authorization: Bearer $QUIVER_KEY"`}</Code>
        </Entry>
      </Section>

      <Section eyebrow="Discovery" title="Search, activity & orientation">
        <Entry header={<Method verb="GET" path="/api/files/search" />} pills={R} desc="Free-text recall over titles and data-field values.">
          <Code label="response">{`curl "https://quiveros.ai/api/files/search?q=Alice&type=score&limit=5" \\
  -H "Authorization: Bearer $QUIVER_KEY"
# → { "results": [ { "id": "…", "title": "Alice · 250", "type": "score", "status": "ended" } ] }`}</Code>
        </Entry>

        <Entry header={<Method verb="GET" path="/api/activity" />} pills={R} desc="Recent changes across the workspace (field, value, actor, time).">
          <Code label="request">{`curl "https://quiveros.ai/api/activity?limit=20" -H "Authorization: Bearer $QUIVER_KEY"`}</Code>
        </Entry>

        <Entry header={<Method verb="GET" path="/api/orient" />} pills={R} desc="A one-call bootstrap bundle — purpose, singletons, active files (children rolled up under their active parent: status counts plus the open ones with their summary fields; a file linked from an active parent is listed there instead of again at the top level — active_linked says which), per-type counts, recent activity. Built for agents starting cold.">
          <Code label="request">{`curl "https://quiveros.ai/api/orient" -H "Authorization: Bearer $QUIVER_KEY"`}</Code>
        </Entry>
      </Section>

      <Section eyebrow="Workspace" title="Inspect the workspace & its config">
        <Entry header={<Method verb="GET" path="/api/workspace" />} pills={R} desc="Workspace metadata; ?include=config returns the full schema (types, fields, statuses, metrics, views).">
          <ParamTable rows={[['include', 'string', 'config · apikeys · secrets · variables']]} />
          <Code label="response">{`curl "https://quiveros.ai/api/workspace" -H "Authorization: Bearer $QUIVER_KEY"
# → { "workspace": { "id": "…", "name": "Quiver Archer", "configId": "personal.leaderboard", "userCount": 1 } }`}</Code>
        </Entry>
      </Section>
    </div>
  )
}
